/**
 * Phase 659 — Supplier Credit Notes tab (reusable across supplier sections).
 *
 * Mount this component anywhere a supplier-invoice list lives (Bills,
 * Elevator Expenses, Repairs, Fire Extinguishers, Insurance, General
 * Expenses) by passing the `section` prop. The component handles its own
 * CRUD against `/api/supplier-credit-notes`.
 *
 * Props:
 *   buildingId — required
 *   section    — bills | elevator | repairs | fire_extinguisher | insurance | expense
 *   suppliers  — array of supplier names available for the picker (optional, falls back to free-text)
 *   invoices   — array of {id, label} for the Related Invoice picker (optional)
 *   t          — translation function
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Receipt, PencilSimple, Trash, MagnifyingGlass, X } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmtMoney = (n) => `€${(parseFloat(n) || 0).toFixed(2)}`;
const fmtDateDmy = (s) => {
  if (!s) return '';
  const parts = String(s).slice(0, 10).split('-');
  return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : s;
};

export default function SupplierCreditNotesTab({ buildingId, section = 'bills', suppliers = [], invoices = [], t }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [search, setSearch] = useState('');

  const fetchRows = useCallback(async () => {
    if (!buildingId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/supplier-credit-notes?building_id=${buildingId}&section=${section}`);
      setRows(data.credit_notes || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || (t?.('errorLoading') || 'Could not load credit notes'));
    } finally { setLoading(false); }
  }, [buildingId, section, t]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  // Phase 662 — Related-invoice dropdown filters to UNPAID / PARTIALLY-PAID
  // invoices only (`outstanding > 0.005`). When editing an existing credit
  // note, every invoice already referenced in its allocations is kept
  // available even if its `outstanding` is reported as 0 (so the form
  // doesn't silently unlink an existing credit). Each option is rendered
  // with a clear single-line label: "Invoice 001167 — 04/06/2026 —
  // Outstanding: €606.90".
  const visibleInvoices = useMemo(() => {
    const editingAllocIds = new Set([
      ...((editing?.allocations || []).map(a => a.invoice_id)),
      editing?.related_invoice_id || '',
    ].filter(Boolean));
    return (invoices || []).filter(inv => {
      if (editingAllocIds.has(inv.id)) return true;
      const out = Number(inv.outstanding ?? inv.amount ?? 0);
      return out > 0.005;
    });
  }, [invoices, editing]);

  // Phase 661 — Live client-side filter over Credit-Note rows. Searches
  // across CN number, related invoice id, notes, supplier and date so the
  // user can find a credit by any of the fields visible in the table.
  const filteredRows = useMemo(() => {
    const q = (search || '').trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r => {
      const hay = [
        r.credit_note_number, r.related_invoice_id, r.notes, r.supplier,
        r.date, fmtDateDmy(r.date),
      ].map(v => String(v || '').toLowerCase()).join(' \u00b7 ');
      return hay.includes(q);
    });
  }, [rows, search]);

  // Phase 662 — Auto-VAT helpers. When `auto_vat` is ON, the user enters
  // the GROSS total and we derive net + VAT amount from the configured
  // VAT rate. When OFF, the user manages amount + VAT independently.
  const recalcAutoVat = (grossTotal, rate) => {
    const g = Number(grossTotal) || 0;
    const r = Math.max(0, Number(rate) || 0);
    const net = g / (1 + r / 100);
    const vat = g - net;
    return { amount: Math.round(net * 100) / 100, vat_amount: Math.round(vat * 100) / 100 };
  };

  const openCreate = () => {
    setEditing({});
    setForm({
      credit_note_number: '', date: new Date().toISOString().slice(0, 10),
      supplier: '', amount: 0, vat_amount: 0, notes: '', attachments: [],
      auto_vat: true, vat_rate: 19, gross_total: 0,
      allocations: [{ invoice_id: '', amount: 0 }],
    });
  };
  const openEdit = (r) => {
    const allocs = (r.allocations && r.allocations.length > 0)
      ? r.allocations.map(a => ({ invoice_id: a.invoice_id, amount: a.amount }))
      : (r.related_invoice_id
          ? [{ invoice_id: r.related_invoice_id, amount: r.applied_amount || (r.amount + r.vat_amount) || 0 }]
          : [{ invoice_id: '', amount: 0 }]);
    const gross = (r.amount || 0) + (r.vat_amount || 0);
    // Auto-VAT defaults OFF when editing so the user sees the stored
    // values exactly as they are.
    setEditing(r);
    setForm({
      credit_note_number: r.credit_note_number || '', date: r.date || '',
      supplier: r.supplier || '',
      amount: r.amount || 0, vat_amount: r.vat_amount || 0,
      notes: r.notes || '', attachments: r.attachments || [],
      auto_vat: false, vat_rate: 19, gross_total: gross,
      allocations: allocs,
    });
  };

  const save = async () => {
    if (!form.credit_note_number?.trim()) { toast.error(t?.('creditNoteNumberRequired') || 'Credit Note number is required'); return; }
    if (!form.supplier?.trim()) { toast.error(t?.('supplierRequired') || 'Supplier is required'); return; }
    // Phase 662 — Build the payload. When auto_vat is on we derive
    // amount + vat from the gross total + configured rate; otherwise the
    // user-typed amount/vat go through unchanged.
    let amountVal = Number(form.amount) || 0;
    let vatVal = Number(form.vat_amount) || 0;
    if (form.auto_vat) {
      const recalced = recalcAutoVat(form.gross_total, form.vat_rate);
      amountVal = recalced.amount;
      vatVal = recalced.vat_amount;
    }
    const total = amountVal + vatVal;
    if (total <= 0) { toast.error(t?.('creditNoteAmountRequired') || 'Amount must be greater than zero'); return; }
    // Clean up allocations: drop blank rows, sum and validate
    const cleanAllocs = (form.allocations || [])
      .filter(a => a.invoice_id && Number(a.amount) > 0.005)
      .map(a => ({ invoice_id: a.invoice_id, amount: Math.round(Number(a.amount) * 100) / 100, section }));
    const allocSum = cleanAllocs.reduce((s, a) => s + a.amount, 0);
    if (allocSum > total + 0.005) {
      toast.error(t?.('allocationsExceedTotal') || `Allocations (${allocSum.toFixed(2)}) exceed credit note amount (${total.toFixed(2)})`);
      return;
    }
    const payload = {
      credit_note_number: form.credit_note_number,
      date: form.date,
      supplier: form.supplier,
      amount: amountVal,
      vat_amount: vatVal,
      notes: form.notes || '',
      attachments: form.attachments || [],
      allocations: cleanAllocs,
    };
    try {
      if (editing?.credit_note_id) {
        await axios.put(`${API}/supplier-credit-notes/${editing.credit_note_id}`, payload);
        toast.success(t?.('creditNoteUpdated') || 'Credit note updated');
      } else {
        await axios.post(`${API}/supplier-credit-notes`, { ...payload, building_id: buildingId, section });
        toast.success(t?.('creditNoteCreated') || 'Credit note recorded');
      }
      setEditing(null);
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || (t?.('saveFailed') || 'Save failed'));
    }
  };

  const confirmRemove = async () => {
    if (!confirmDelete) return;
    try {
      await axios.delete(`${API}/supplier-credit-notes/${confirmDelete.credit_note_id}`);
      toast.success(t?.('creditNoteDeleted') || 'Credit note deleted');
      setConfirmDelete(null);
      fetchRows();
    } catch (e) { toast.error(e.response?.data?.detail || (t?.('deleteFailed') || 'Delete failed')); }
  };

  if (loading) return <div className="text-sm text-zinc-400 py-8" data-testid="scn-loading">Loading…</div>;

  return (
    <div className="bg-white border border-zinc-200" data-testid="supplier-credit-notes-tab">
      <div className="px-4 py-3 border-b border-zinc-200 bg-zinc-50 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Receipt size={14} className="text-emerald-600" />
          <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-700">{t?.('creditNotesTab') || 'Credit Notes'}</h3>
          <span className="text-[11px] text-zinc-400 font-mono">({filteredRows.length}{search ? `/${rows.length}` : ''})</span>
        </div>
        <div className="flex items-center gap-2 ml-auto">
          {/* Phase 661 — Live search box. Filters across CN #, related
              invoice, notes, supplier and date. */}
          <div className="relative">
            <MagnifyingGlass size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t?.('searchPlaceholderCn') || 'Search number, invoice, notes, date...'}
              className="rounded-none h-7 text-xs pl-7 pr-7 w-72"
              data-testid="scn-search"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 text-zinc-400 hover:text-zinc-700"
                data-testid="scn-search-clear"
              >
                <X size={12} />
              </button>
            )}
          </div>
          <Button onClick={openCreate} className="bg-zinc-950 rounded-none h-7 text-xs px-2.5" data-testid="add-credit-note-btn">
            <Plus size={12} className="mr-1" />{t?.('addCreditNote') || 'Add Credit Note'}
          </Button>
        </div>
      </div>
      {filteredRows.length === 0 ? (
        <div className="px-4 py-12 text-center text-sm text-zinc-400" data-testid="scn-empty">
          {search
            ? (t?.('noMatchesForSearch') || `No credit notes match "${search}"`)
            : (t?.('noCreditNotes') || 'No supplier credit notes recorded yet.')}
        </div>
      ) : (
        <div className="divide-y divide-zinc-100">
          <div className="px-4 py-2 grid grid-cols-12 gap-2 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold bg-zinc-50">
            <div className="col-span-1">{t?.('date') || 'Date'}</div>
            <div className="col-span-2">{t?.('creditNoteNumber') || 'CN #'}</div>
            <div className="col-span-3">{t?.('supplier') || 'Supplier'}</div>
            <div className="col-span-2">{t?.('relatedInvoice') || 'Related Invoice'}</div>
            <div className="col-span-1 text-right">{t?.('amount') || 'Amount'}</div>
            <div className="col-span-1 text-right">{t?.('vat') || 'VAT'}</div>
            <div className="col-span-1 text-right">{t?.('remaining') || 'Remaining'}</div>
            <div className="col-span-1 text-right">{t?.('actions') || 'Actions'}</div>
          </div>
          {filteredRows.map(r => {
            // Resolve a human-readable label for the Related Invoice
            // column. With Phase 662 a CN can have multiple allocations
            // → render "Invoice X + Y (n)" so the user instantly sees
            // multi-allocation rows in the list.
            const allocs = (r.allocations && r.allocations.length > 0)
              ? r.allocations
              : (r.related_invoice_id ? [{ invoice_id: r.related_invoice_id }] : []);
            let relLabel = '';
            if (allocs.length === 0) {
              relLabel = '';
            } else if (allocs.length === 1) {
              const inv = (invoices || []).find(i => i.id === allocs[0].invoice_id);
              relLabel = inv?.number || allocs[0].invoice_id || '';
            } else {
              const firstTwo = allocs.slice(0, 2).map(a => {
                const inv = (invoices || []).find(i => i.id === a.invoice_id);
                return inv?.number || (a.invoice_id ? a.invoice_id.slice(-6) : '—');
              }).join(' + ');
              relLabel = `${firstTwo}${allocs.length > 2 ? ` +${allocs.length - 2}` : ''} (${allocs.length})`;
            }
            return (
              <div key={r.credit_note_id} className="px-4 py-2 grid grid-cols-12 gap-2 items-center text-xs hover:bg-zinc-50" data-testid={`scn-row-${r.credit_note_id}`}>
                <div className="col-span-1 font-mono text-[11px] text-zinc-600">{fmtDateDmy(r.date)}</div>
                <div className="col-span-2 font-mono font-semibold text-emerald-700">{r.credit_note_number}</div>
                <div className="col-span-3 truncate">{r.supplier || '—'}</div>
                <div className="col-span-2 truncate text-zinc-500 text-[11px] font-mono" title={relLabel}>{relLabel || '—'}</div>
                <div className="col-span-1 text-right font-mono text-red-700">−{fmtMoney(r.amount)}</div>
                <div className="col-span-1 text-right font-mono text-red-700">−{fmtMoney(r.vat_amount)}</div>
                <div className="col-span-1 text-right font-mono">{r.remaining_credit > 0.01 ? <span className="text-amber-600 font-bold">{fmtMoney(r.remaining_credit)}</span> : <span className="text-zinc-400">{fmtMoney(0)}</span>}</div>
                <div className="col-span-1 flex items-center justify-end gap-0.5">
                  <button onClick={() => openEdit(r)} className="p-1 hover:bg-blue-50 text-zinc-400 hover:text-blue-600" title={t?.('edit') || 'Edit'} data-testid={`scn-edit-${r.credit_note_id}`}><PencilSimple size={12} /></button>
                  <button onClick={() => setConfirmDelete(r)} className="p-1 hover:bg-red-50 text-zinc-400 hover:text-red-600" title={t?.('delete') || 'Delete'} data-testid={`scn-delete-${r.credit_note_id}`}><Trash size={12} /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing !== null && (
        <Dialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
          <DialogContent className="rounded-none w-[95vw] max-w-[700px] sm:!max-w-[700px] p-0" data-testid="scn-dialog">
            <DialogHeader className="px-5 pt-4 pb-3 border-b border-zinc-200">
              <DialogTitle className="text-sm" style={{ fontFamily: 'var(--font-heading)' }}>
                {editing?.credit_note_id ? (t?.('editCreditNote') || 'Edit Credit Note') : (t?.('recordCreditNote') || 'Record Supplier Credit Note')}
              </DialogTitle>
            </DialogHeader>
            <div className="px-5 py-4 grid grid-cols-2 gap-3 max-h-[70vh] overflow-y-auto">
              <div><Label className="text-[10px] uppercase tracking-wide text-zinc-500">{(t?.('creditNoteNumber') || 'Credit Note #')} *</Label><Input value={form.credit_note_number || ''} onChange={e => setForm({ ...form, credit_note_number: e.target.value })} className="rounded-none mt-0.5 h-8 text-sm font-mono" placeholder={t?.('supplierCnPlaceholder') || "Supplier's CN number"} data-testid="scn-number" /></div>
              <div><Label className="text-[10px] uppercase tracking-wide text-zinc-500">{(t?.('date') || 'Date')} *</Label><Input type="date" value={form.date || ''} onChange={e => setForm({ ...form, date: e.target.value })} className="rounded-none mt-0.5 h-8 text-sm font-mono" data-testid="scn-date" /></div>
              <div className="col-span-2">
                <Label className="text-[10px] uppercase tracking-wide text-zinc-500">{(t?.('supplier') || 'Supplier')} *</Label>
                {suppliers.length > 0 ? (
                  <input list="scn-suppliers" value={form.supplier || ''} onChange={e => setForm({ ...form, supplier: e.target.value })} className="rounded-none mt-0.5 h-8 text-sm w-full border border-zinc-300 px-2" data-testid="scn-supplier" />
                ) : (
                  <Input value={form.supplier || ''} onChange={e => setForm({ ...form, supplier: e.target.value })} className="rounded-none mt-0.5 h-8 text-sm" data-testid="scn-supplier" />
                )}
                <datalist id="scn-suppliers">{suppliers.map(s => <option key={s} value={s} />)}</datalist>
              </div>
              {/* Phase 662 — Auto-VAT toggle + Total Amount field.
                  When ON, the user types the GROSS total and the system
                  splits it into Net + VAT using the configured VAT rate.
                  When OFF, Net + VAT inputs are editable independently. */}
              <div className="col-span-2 bg-zinc-50 border border-zinc-200 px-3 py-2.5">
                <div className="flex items-center justify-between gap-3 mb-2">
                  <label className="flex items-center gap-2 text-xs font-medium text-zinc-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!form.auto_vat}
                      onChange={e => {
                        const auto = e.target.checked;
                        if (auto) {
                          // Switching to auto: derive gross from current
                          // amount + VAT so the gross field starts populated.
                          const gross = (Number(form.amount) || 0) + (Number(form.vat_amount) || 0);
                          const recalced = recalcAutoVat(gross, form.vat_rate);
                          setForm({ ...form, auto_vat: true, gross_total: gross, amount: recalced.amount, vat_amount: recalced.vat_amount });
                        } else {
                          setForm({ ...form, auto_vat: false });
                        }
                      }}
                      data-testid="scn-auto-vat"
                    />
                    <span>{t?.('autoCalculateVat') || 'Auto calculate VAT'}</span>
                  </label>
                  <div className="flex items-center gap-1">
                    <Label className="text-[10px] uppercase tracking-wide text-zinc-500">{t?.('vatRate') || 'VAT rate'} %</Label>
                    <Input
                      type="number" step="0.01" min="0"
                      value={form.vat_rate ?? 0}
                      onChange={e => {
                        const rate = parseFloat(e.target.value) || 0;
                        if (form.auto_vat) {
                          const r = recalcAutoVat(form.gross_total, rate);
                          setForm({ ...form, vat_rate: rate, amount: r.amount, vat_amount: r.vat_amount });
                        } else {
                          setForm({ ...form, vat_rate: rate });
                        }
                      }}
                      className="rounded-none h-7 text-xs w-20 font-mono"
                      data-testid="scn-vat-rate"
                    />
                  </div>
                </div>
                {form.auto_vat ? (
                  <div className="grid grid-cols-3 gap-3 items-end">
                    <div>
                      <Label className="text-[10px] uppercase tracking-wide text-zinc-500">{t?.('totalAmount') || 'Total Amount'} *</Label>
                      <Input
                        type="number" step="0.01" min="0"
                        value={form.gross_total ?? 0}
                        onChange={e => {
                          const g = parseFloat(e.target.value) || 0;
                          const r = recalcAutoVat(g, form.vat_rate);
                          setForm({ ...form, gross_total: g, amount: r.amount, vat_amount: r.vat_amount });
                        }}
                        className="rounded-none mt-0.5 h-8 text-sm font-mono"
                        data-testid="scn-gross-total"
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-wide text-zinc-500">{t?.('netAmount') || 'Net'}</Label>
                      <div className="rounded-none mt-0.5 h-8 text-sm font-mono px-2 flex items-center bg-white border border-zinc-200 text-zinc-600" data-testid="scn-net-derived">{fmtMoney(form.amount || 0)}</div>
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-wide text-zinc-500">{t?.('vat') || 'VAT'}</Label>
                      <div className="rounded-none mt-0.5 h-8 text-sm font-mono px-2 flex items-center bg-white border border-zinc-200 text-zinc-600" data-testid="scn-vat-derived">{fmtMoney(form.vat_amount || 0)}</div>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-[10px] uppercase tracking-wide text-zinc-500">{(t?.('amountExclVat') || 'Amount (excl. VAT)')} *</Label>
                      <Input type="number" step="0.01" min="0" value={form.amount ?? 0} onChange={e => setForm({ ...form, amount: parseFloat(e.target.value) || 0 })} className="rounded-none mt-0.5 h-8 text-sm font-mono" data-testid="scn-amount" />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase tracking-wide text-zinc-500">{t?.('vat') || 'VAT'} ({t?.('manual') || 'manual'})</Label>
                      <Input type="number" step="0.01" min="0" value={form.vat_amount ?? 0} onChange={e => setForm({ ...form, vat_amount: parseFloat(e.target.value) || 0 })} className="rounded-none mt-0.5 h-8 text-sm font-mono" data-testid="scn-vat" />
                    </div>
                  </div>
                )}
              </div>

              {/* Phase 662 — Multi-allocation. The user can split the Credit
                  Note amount across several unpaid / partially-paid invoices.
                  We keep ONE add row at a time, plus a running "Allocated /
                  Remaining" footer so the user knows when they've fully
                  consumed the credit. */}
              <div className="col-span-2">
                <div className="flex items-center justify-between mb-1.5">
                  <Label className="text-[10px] uppercase tracking-wide text-zinc-500">{t?.('allocateToInvoices') || 'Allocate to Invoices (optional)'}</Label>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, allocations: [...(form.allocations || []), { invoice_id: '', amount: 0 }] })}
                    className="text-[11px] text-emerald-700 hover:text-emerald-900 font-semibold"
                    data-testid="scn-add-allocation"
                  >+ {t?.('addAllocation') || 'Add Allocation'}</button>
                </div>
                <div className="space-y-1.5">
                  {(form.allocations || []).map((alloc, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`scn-alloc-row-${idx}`}>
                      <select
                        value={alloc.invoice_id}
                        onChange={e => {
                          const next = [...form.allocations];
                          next[idx] = { ...next[idx], invoice_id: e.target.value };
                          // Auto-fill amount with the invoice's outstanding
                          // on first selection so the common single-invoice
                          // case requires zero typing.
                          if (e.target.value && (!next[idx].amount || next[idx].amount === 0)) {
                            const inv = visibleInvoices.find(i => i.id === e.target.value);
                            if (inv) {
                              const out = Number(inv.outstanding || 0);
                              const total = (Number(form.amount) || 0) + (Number(form.vat_amount) || 0);
                              const otherSum = next.filter((_, j) => j !== idx).reduce((s, a) => s + (Number(a.amount) || 0), 0);
                              const remaining = Math.max(0, total - otherSum);
                              next[idx].amount = Math.round(Math.min(out, remaining) * 100) / 100;
                            }
                          }
                          setForm({ ...form, allocations: next });
                        }}
                        className="col-span-8 rounded-none h-8 text-xs border border-zinc-300 px-2 bg-white"
                        data-testid={`scn-alloc-invoice-${idx}`}
                      >
                        <option value="">{t?.('selectInvoice') || '— Select invoice —'}</option>
                        {visibleInvoices.map(i => {
                          const num = i.number || (i.id ? i.id.slice(-8) : '—');
                          const d = fmtDateDmy(i.date);
                          const outstanding = Number(i.outstanding ?? 0);
                          const outLabel = outstanding > 0.005
                            ? `${t?.('outstanding') || 'Outstanding'}: ${fmtMoney(outstanding)}`
                            : (t?.('paid') || 'Paid');
                          const label = [`${t?.('invoice') || 'Invoice'} ${num}`, d ? `Date: ${d}` : null, outLabel].filter(Boolean).join('  \u2014  ');
                          return <option key={i.id} value={i.id}>{label}</option>;
                        })}
                      </select>
                      <Input
                        type="number" step="0.01" min="0"
                        value={alloc.amount}
                        onChange={e => {
                          const next = [...form.allocations];
                          next[idx] = { ...next[idx], amount: parseFloat(e.target.value) || 0 };
                          setForm({ ...form, allocations: next });
                        }}
                        className="col-span-3 rounded-none h-8 text-xs font-mono text-right"
                        placeholder="0.00"
                        data-testid={`scn-alloc-amount-${idx}`}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const next = form.allocations.filter((_, j) => j !== idx);
                          setForm({ ...form, allocations: next.length ? next : [{ invoice_id: '', amount: 0 }] });
                        }}
                        className="col-span-1 p-1 text-zinc-400 hover:text-red-600"
                        title={t?.('remove') || 'Remove'}
                        data-testid={`scn-alloc-remove-${idx}`}
                      ><Trash size={12} /></button>
                    </div>
                  ))}
                </div>
                {(() => {
                  const total = (Number(form.amount) || 0) + (Number(form.vat_amount) || 0);
                  const allocated = (form.allocations || []).reduce((s, a) => s + (Number(a.amount) || 0), 0);
                  const remaining = total - allocated;
                  const exceeds = remaining < -0.005;
                  return (
                    <div className="mt-2 flex items-center justify-end gap-4 text-[11px] font-mono">
                      <span className="text-zinc-500">{t?.('total') || 'Total'}: <span className="text-zinc-800 font-semibold">{fmtMoney(total)}</span></span>
                      <span className="text-zinc-500">{t?.('allocated') || 'Allocated'}: <span className="text-zinc-800 font-semibold" data-testid="scn-alloc-sum">{fmtMoney(allocated)}</span></span>
                      <span className={exceeds ? 'text-red-700 font-bold' : (remaining > 0.005 ? 'text-amber-700' : 'text-emerald-700')}>
                        {t?.('remaining') || 'Remaining'}: <span className="font-semibold" data-testid="scn-alloc-remaining">{fmtMoney(Math.max(0, remaining))}</span>{exceeds && <span className="ml-1">⚠</span>}
                      </span>
                    </div>
                  );
                })()}
                <p className="text-[10px] text-zinc-400 mt-1">{t?.('onlyUnpaidShown') || 'Only unpaid / partially-paid invoices are shown.'}</p>
              </div>

              <div className="col-span-2"><Label className="text-[10px] uppercase tracking-wide text-zinc-500">{t?.('notes') || 'Notes'}</Label><Input value={form.notes || ''} onChange={e => setForm({ ...form, notes: e.target.value })} className="rounded-none mt-0.5 h-8 text-sm" data-testid="scn-notes" /></div>
            </div>
            <div className="border-t border-zinc-200 px-5 py-3 flex items-center justify-end gap-2">
              <Button variant="outline" onClick={() => setEditing(null)} className="rounded-none h-8 text-xs px-3" data-testid="scn-cancel">{t?.('cancel') || 'Cancel'}</Button>
              <Button onClick={save} className="bg-zinc-950 rounded-none h-8 text-xs px-4" data-testid="scn-save">{editing?.credit_note_id ? (t?.('save') || 'Save') : (t?.('record') || 'Record')}</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Phase 659 — Inline delete-confirmation dialog. Replaces native
          window.confirm() which silently breaks inside the Capacitor
          Android wrapper. */}
      {confirmDelete && (
        <Dialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
          <DialogContent className="rounded-none w-[95vw] max-w-[420px] sm:!max-w-[420px] p-0" data-testid="scn-confirm-delete">
            <DialogHeader className="px-5 pt-4 pb-3 border-b border-zinc-200">
              <DialogTitle className="text-sm" style={{ fontFamily: 'var(--font-heading)' }}>
                {t?.('deleteCreditNote') || 'Delete Credit Note'}
              </DialogTitle>
            </DialogHeader>
            <div className="px-5 py-4 text-sm text-zinc-700">
              {t?.('deleteCreditNoteConfirm') || 'Are you sure you want to delete this credit note? The amount will be added back to the supplier balance.'}
              <div className="mt-2 font-mono text-xs text-emerald-700">{confirmDelete.credit_note_number}</div>
            </div>
            <div className="border-t border-zinc-200 px-5 py-3 flex items-center justify-end gap-2">
              <Button variant="outline" onClick={() => setConfirmDelete(null)} className="rounded-none h-8 text-xs px-3" data-testid="scn-confirm-cancel">{t?.('cancel') || 'Cancel'}</Button>
              <Button onClick={confirmRemove} className="bg-red-600 hover:bg-red-700 text-white rounded-none h-8 text-xs px-4" data-testid="scn-confirm-delete-btn">{t?.('delete') || 'Delete'}</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
